/**
 * The app's general date, time and duration formatters. Each one is built
 * once on an `Intl` formatter, so the same kind of value reads the same way
 * on every page. Dates are en-US, like the billing and tokens tables, in
 * the browser's time zone.
 */

/** A moment as the app passes it around: an ISO string, epoch ms or a Date. */
type Instant = Date | string | number;

const toDate = (when: Instant) =>
  when instanceof Date ? when : new Date(when);

const relativeTime = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["week", 7 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
  ["hour", 60 * 60],
  ["minute", 60],
];

/** "3 days ago", "yesterday", "just now". */
export function formatRelative(
  when: Instant,
  now: Instant = new Date(),
): string {
  const seconds = Math.round(
    (toDate(when).getTime() - toDate(now).getTime()) / 1000,
  );
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) {
      return relativeTime.format(Math.trunc(seconds / size), unit);
    }
  }
  return "just now";
}

const dateTime = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** "Oct 14, 2026, 1:05 PM": for a moment a date alone cannot place. */
export function formatDateTime(when: Instant): string {
  return dateTime.format(toDate(when));
}

const date = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

/** "Oct 14, 2026" */
export function formatDate(when: Instant): string {
  return date.format(toDate(when));
}

// h23 rather than hour12: false, which some engines render as "24:05".
const clock = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** "13:05", "09:30" */
export function formatClock(when: Instant): string {
  return clock.format(toDate(when));
}

const clockSeconds = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/** "18:34:02": a moment to the second, such as when a message was posted. */
export function formatClockSeconds(when: Instant): string {
  return clockSeconds.format(toDate(when));
}

const day = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
});

/** "Wed, Oct 14" */
export function formatDay(when: Instant): string {
  return day.format(toDate(when));
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Midnights crossed between two moments in the local calendar: 0 for the
 * same day, 1 for yesterday-to-today however few hours apart.
 */
export function calendarDaysBetween(from: Instant, to: Instant): number {
  const start = toDate(from);
  const end = toDate(to);
  const startDay = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  // Rounded because a daylight-saving change makes a day 23 or 25 hours.
  return Math.round((endDay.getTime() - startDay.getTime()) / MS_PER_DAY);
}

const unitFormat = (
  unit: "day" | "hour" | "minute" | "second",
  unitDisplay: "long" | "short",
) => new Intl.NumberFormat("en-US", { style: "unit", unit, unitDisplay });

const longUnits = {
  hour: unitFormat("hour", "long"),
  minute: unitFormat("minute", "long"),
  second: unitFormat("second", "long"),
};

export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;

/**
 * A rule's timing in words: "5 minutes", "1 hour 30 minutes", "45 seconds".
 * Zero reads "immediately", because every timing field treats 0 as "do not
 * wait".
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total === 0) return "immediately";
  const hours = Math.floor(total / SECONDS_PER_HOUR);
  const minutes = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const rest = total % SECONDS_PER_MINUTE;
  return [
    hours > 0 && longUnits.hour.format(hours),
    minutes > 0 && longUnits.minute.format(minutes),
    rest > 0 && longUnits.second.format(rest),
  ]
    .filter((part) => part !== false)
    .join(" ");
}

const shortUnits = {
  day: unitFormat("day", "short"),
  hour: unitFormat("hour", "short"),
  minute: unitFormat("minute", "short"),
};

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/**
 * How long something lasted, to the minute and in at most two units:
 * "41 min", "2 hr 5 min", "1 day 3 hr". Shorter than a minute reads
 * "under 1 min" rather than "0 min".
 */
export function formatSpan(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes === 0) return `under ${shortUnits.minute.format(1)}`;
  if (minutes < MINUTES_PER_HOUR) return shortUnits.minute.format(minutes);
  if (minutes < MINUTES_PER_DAY) {
    const hours = Math.floor(minutes / MINUTES_PER_HOUR);
    const rest = minutes % MINUTES_PER_HOUR;
    return rest === 0
      ? shortUnits.hour.format(hours)
      : `${shortUnits.hour.format(hours)} ${shortUnits.minute.format(rest)}`;
  }
  const days = Math.floor(minutes / MINUTES_PER_DAY);
  const hours = Math.floor((minutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  return hours === 0
    ? shortUnits.day.format(days)
    : `${shortUnits.day.format(days)} ${shortUnits.hour.format(hours)}`;
}

const countNumber = new Intl.NumberFormat("en-US");

/** A whole count with thousands separators: "1,234". */
export function formatCount(count: number): string {
  return countNumber.format(count);
}
